import React from 'react';
import { FiDollarSign, FiTrendingUp, FiTrendingDown, FiMinus, FiPlus, FiTrash2 } from 'react-icons/fi';
import { generateId } from '@/utils/helpers';
import { calculateTotalRevenuesEntrepreneur } from '@/utils/financialCalculations';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGridThree,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  RadioGroup,
  RadioLabel,
  InfoBox,
  HelpText,
  CalculatedValue,
  CalculatedLabel,
  CalculatedAmount,
  DynamicEntryContainer,
  EntryHeader,
  EntryTitle,
  EntryNumber,
  RemoveButton,
  AddButton
} from '../common/FormStyles';
import { BusinessDebt } from '@/types/application-forms';
import { Candidate } from '@/models';

interface BusinessFinancialsSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;
}

export const BusinessFinancialsSection: React.FC<BusinessFinancialsSectionProps> = ({
  data,
  onChange,
  readonly = false
}) => {

  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleDebtChange = (index: number, field: string, value: any) => {
    const newDettes = [...(data.dettes || [])];
    newDettes[index] = { ...newDettes[index], [field]: value };
    handleChange('dettes', newDettes);
  };

  const addDebt = () => {
    const newDebt: BusinessDebt = {
      id: generateId(),
      type: 'Crédit fournisseurs',
      montant_total: 0,
      mensualite: 0
    };
    handleChange('dettes', [...(data.dettes || []), newDebt]);
  };

  const removeDebt = (index: number) => {
    const newDettes = (data.dettes || []).filter((_, i) => i !== index);
    handleChange('dettes', newDettes);
  };

  const calculateTotalChargesBusiness = () => {
    const {
      loyer_local = 0,
      salaires_main_oeuvre = 0,
      marchandises_stock = 0,
      charges_business_transport = 0,
      taxes_patente = 0,
      autres_charges_business = 0
    } = data;

    return (
      Number(loyer_local || 0) +
      Number(salaires_main_oeuvre || 0) +
      Number(marchandises_stock || 0) +
      Number(charges_business_transport??0) +
      Number(taxes_patente || 0) +
      Number(autres_charges_business || 0)
    );
  };

  // Calculs automatiques utilisant les fonctions utilitaires centralisées
  const totalCharges = calculateTotalChargesBusiness();
  const totalRevenues = calculateTotalRevenuesEntrepreneur(data);

  // Mettre à jour les totaux calculés
  React.useEffect(() => {
    if (data.total_charges_business !== totalCharges) {
      handleChange('total_charges_business', totalCharges);
    }
     if(data.ventes_mensuelles && data.total_charges_business){
      handleChange('benefice_net_mensuel',(data.ventes_mensuelles||0) -( data.total_charges_business ||0 ))
    }
  }, [totalCharges]);

  React.useEffect(() => {
    if (data.total_revenus !== totalRevenues) {
      handleChange('total_revenus', totalRevenues);
    }

   
  }, [totalRevenues]);

  return (
    <>
      <FormSection>
        <SectionTitle>
          <FiDollarSign />
          Situation Financière de l'Entreprise
        </SectionTitle>

        <InfoBox $type="info">
          <p>
            Renseignez les informations financières de votre entreprise de manière aussi précise que possible.
            Ces informations sont cruciales pour évaluer la viabilité de votre activité.
          </p>
        </InfoBox>

        <SubSectionTitle>Chiffre d'Affaires</SubSectionTitle>

        <FormGrid>
          <FormGroup>
            <Label>Ventes moyennes journalières (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.ventes_journalieres || ''}
              onChange={e => handleChange('ventes_journalieres', parseInt(e.target.value) || 0)}
              placeholder="Ex: 50000"
              disabled={readonly}
            />
            <HelpText>Moyenne de vos ventes par jour</HelpText>
          </FormGroup>

          <FormGroup>
            <Label $required>Ventes moyennes mensuelles (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.ventes_mensuelles || ''}
              onChange={e => handleChange('ventes_mensuelles', parseInt(e.target.value) || 0)}
              placeholder="Ex: 1500000"
              disabled={readonly}
            />
            <HelpText>Chiffre d'affaires moyen par mois</HelpText>
          </FormGroup>
        </FormGrid>

        <FormGrid>
          <FormGroup>
            <Label>Meilleur mois (haute saison) (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.montant_meilleure_periode || ''}
              onChange={e => handleChange('montant_meilleure_periode', parseInt(e.target.value) || 0)}
              placeholder="Ex: 3000000"
              disabled={readonly}
            />
            <HelpText>Votre meilleur mois de ventes</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Plus faible mois (basse saison) (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.montant_periode_difficile || ''}
              onChange={e => handleChange('montant_periode_difficile', parseInt(e.target.value) || 0)}
              placeholder="Ex: 800000"
              disabled={readonly}
            />
            <HelpText>Votre mois le plus faible</HelpText>
          </FormGroup>
        </FormGrid>

        <FormGroup>
          <Label>Évolution de l'activité</Label>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                value="En hausse"
                checked={data.evolution_activite === 'En hausse'}
                onChange={e => handleChange('evolution_activite', e.target.value)}
                disabled={readonly}
              />
              <FiTrendingUp style={{ color: '#10b981', marginRight: '8px' }} />
              En hausse
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                value="Stable"
                checked={data.evolution_activite === 'Stable'}
                onChange={e => handleChange('evolution_activite', e.target.value)}
                disabled={readonly}
              />
              <FiMinus style={{ color: '#6b7280', marginRight: '8px' }} />
              Stable
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                value="En baisse"
                checked={data.evolution_activite === 'En baisse'}
                onChange={e => handleChange('evolution_activite', e.target.value)}
                disabled={readonly}
              />
              <FiTrendingDown style={{ color: '#ef4444', marginRight: '8px' }} />
              En baisse
            </RadioLabel>
          </RadioGroup>
        </FormGroup>

        {data.evolution_activite && data.evolution_activite !== 'Stable' && (
          <FormGroup>
            <Label>Pourcentage d'évolution</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={data.pourcentage_evolution || ''}
              onChange={e => handleChange('pourcentage_evolution', parseInt(e.target.value))}
              placeholder="Ex: 20"
              disabled={readonly}
            />
            <HelpText>
              Pourcentage approximatif de {data.evolution_activite === 'En hausse' ? 'hausse' : 'baisse'}
            </HelpText>
          </FormGroup>
        )}

        <SubSectionTitle>Charges Mensuelles de l'Entreprise</SubSectionTitle>

        <FormGrid>
          <FormGroup>
            <Label>Loyer du local (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.loyer_local || ''}
              onChange={e => handleChange('loyer_local', parseInt(e.target.value) || 0)}
              placeholder="Ex: 50000"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Salaires et main d'œuvre (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.salaires_main_oeuvre || ''}
              onChange={e => handleChange('salaires_main_oeuvre', parseInt(e.target.value) || 0)}
              placeholder="Ex: 100000"
              disabled={readonly}
            />
            <HelpText>Total des salaires payés</HelpText>
          </FormGroup>
        </FormGrid>

        <FormGrid>
          <FormGroup>
            <Label>Marchandises/Stock (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.marchandises_stock || ''}
              onChange={e => handleChange('marchandises_stock', parseInt(e.target.value) || 0)}
              placeholder="Ex: 500000"
              disabled={readonly}
            />
            <HelpText>Achats mensuels de marchandises</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Transport (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.charges_business_transport || ''}
              onChange={e => handleChange('charges_business_transport', parseInt(e.target.value) || 0)}
              placeholder="Ex: 30000"
              disabled={readonly}
            />
            <HelpText>Frais de transport mensuels</HelpText>
          </FormGroup>
        </FormGrid>

        <FormGrid>
          <FormGroup>
            <Label>Taxes/Patente (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.taxes_patente || ''}
              onChange={e => handleChange('taxes_patente', parseInt(e.target.value) || 0)}
              placeholder="Ex: 15000"
              disabled={readonly}
            />
            <HelpText>Taxes mensuelles (ou montant annuel divisé par 12)</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Autres charges (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.autres_charges_business || ''}
              onChange={e => handleChange('autres_charges_business', parseInt(e.target.value) || 0)}
              placeholder="Ex: 20000"
              disabled={readonly}
            />
          </FormGroup>
        </FormGrid>

        

        <CalculatedValue>
          <CalculatedLabel>Total charges mensuelles</CalculatedLabel>
          <CalculatedAmount>{totalCharges.toLocaleString()} FCFA</CalculatedAmount>
        </CalculatedValue>

        <SubSectionTitle>Bénéfices</SubSectionTitle>

        <FormGrid>
          <FormGroup>
            <Label>Bénéfice net moyen mensuel (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.benefice_net_mensuel || ''}
              onChange={e => handleChange('benefice_net_mensuel', parseInt(e.target.value) || 0)}
              placeholder="Ex: 300000"
              disabled={readonly}
            />
            <HelpText>Ventes - Charges = Bénéfice</HelpText>
          </FormGroup>

          <FormGroup>
            <Label $required>Ce que vous tirez personnellement par mois (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.revenu_personnel_tire || ''}
              onChange={e => handleChange('revenu_personnel_tire', parseInt(e.target.value) || 0)}
              placeholder="Ex: 200000"
              disabled={readonly}
            />
            <HelpText>Montant que vous prélevez pour vos besoins personnels</HelpText>
          </FormGroup>
        </FormGrid>

        <SubSectionTitle>Capital et Stock</SubSectionTitle>

        <FormGrid>
          <FormGroup>
            <Label>Valeur du stock actuel (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.valeur_stock || ''}
              onChange={e => handleChange('valeur_stock', parseInt(e.target.value) || 0)}
              placeholder="Ex: 2000000"
              disabled={readonly}
            />
            <HelpText>Valeur estimée de votre stock de marchandises</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Valeur équipements/matériel (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.valeur_equipements || ''}
              onChange={e => handleChange('valeur_equipements', parseInt(e.target.value) || 0)}
              placeholder="Ex: 1500000"
              disabled={readonly}
            />
            <HelpText>Valeur de vos équipements, machines, outils...</HelpText>
          </FormGroup>
        </FormGrid>

        <FormGrid>
          <FormGroup>
            <Label>Fonds de roulement (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.fonds_roulement || ''}
              onChange={e => handleChange('fonds_roulement', parseInt(e.target.value) || 0)}
              placeholder="Ex: 500000"
              disabled={readonly}
            />
            <HelpText>Liquidités disponibles pour l'activité</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Créances clients (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.creances_clients || ''}
              onChange={e => handleChange('creances_clients', parseInt(e.target.value) || 0)}
              placeholder="Ex: 300000"
              disabled={readonly}
            />
            <HelpText>Montant que vos clients vous doivent</HelpText>
          </FormGroup>
        </FormGrid>

        <SubSectionTitle>Dettes de l'Entreprise</SubSectionTitle>

        {(data.dettes || []).map((dette, index) => (
          <DynamicEntryContainer key={dette.id}>
            <EntryHeader>
              <EntryTitle>
                <EntryNumber>{index + 1}</EntryNumber>
                Dette {index + 1}
              </EntryTitle>
              <RemoveButton
                onClick={() => removeDebt(index)}
                disabled={readonly}
              >
                <FiTrash2 />
                Supprimer
              </RemoveButton>
            </EntryHeader>

            <FormGridThree>
              <FormGroup>
                <Label>Type de dette</Label>
                <Select
                  value={dette.type || 'Crédit fournisseurs'}
                  onChange={e => handleDebtChange(index, 'type', e.target.value)}
                  disabled={readonly}
                >
                  <option value="Crédit fournisseurs">Crédit fournisseurs</option>
                  <option value="Bancaire">Bancaire</option>
                  <option value="Microfinance">Microfinance</option>
                  <option value="Famille">Famille</option>
                  <option value="Tontine">Tontine</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Montant total (FCFA)</Label>
                <Input
                  type="number"
                  min="0"
                  value={dette.montant_total || ''}
                  onChange={e => handleDebtChange(index, 'montant_total', parseInt(e.target.value) || 0)}
                  placeholder="Ex: 500000"
                  disabled={readonly}
                />
              </FormGroup>

              <FormGroup>
                <Label>Mensualité (FCFA)</Label>
                <Input
                  type="number"
                  min="0"
                  value={dette.mensualite || ''}
                  onChange={e => handleDebtChange(index, 'mensualite', parseInt(e.target.value) || 0)}
                  placeholder="Ex: 50000"
                  disabled={readonly}
                />
              </FormGroup>
            </FormGridThree>
          </DynamicEntryContainer>
        ))}

        {!readonly && (
          <AddButton onClick={addDebt}>
            <FiPlus />
            Ajouter une dette
          </AddButton>
        )}
      </FormSection>

      <FormSection>
        <SectionTitle>
          <FiTrendingUp />
          Vos Revenus Personnels
        </SectionTitle>

        <InfoBox $type="info">
          <p>
            Indiquez tous vos revenus personnels, y compris ce que vous tirez de l'entreprise
            et vos autres sources de revenus.
          </p>
        </InfoBox>

        <FormGrid>
          <FormGroup>
            <Label $required>Tiré de l'entreprise (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.revenu_personnel_tire || ''}
              onChange={e => handleChange('revenu_personnel_tire', parseInt(e.target.value) || 0)}
              placeholder="Ex: 200000"
              disabled={readonly}
            />
            <HelpText>Ce que vous prélevez mensuellement de l'entreprise</HelpText>
          </FormGroup>

          
        </FormGrid>

        <FormGrid>
          <FormGroup>
            <Label>Revenus locatifs (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.revenus_locatifs || ''}
              onChange={e => handleChange('revenus_locatifs', parseInt(e.target.value) || 0)}
              placeholder="Ex: 100000"
              disabled={readonly}
            />
            <HelpText>Loyers perçus si vous avez des biens en location</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Autres revenus (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.autres_revenus || ''}
              onChange={e => handleChange('autres_revenus', parseInt(e.target.value) || 0)}
              placeholder="Ex: 30000"
              disabled={readonly}
            />
          </FormGroup>
        </FormGrid>

        {(data.autres_revenus || 0) > 0 && (
          <FormGroup>
            <Label>Description des autres revenus</Label>
            <TextArea
              value={data.description_autres_revenus || ''}
              onChange={e => handleChange('description_autres_revenus', e.target.value)}
              placeholder="Précisez la source de ces autres revenus..."
              disabled={readonly}
            />
          </FormGroup>
        )}

        <CalculatedValue>
          <CalculatedLabel>Total de vos revenus personnels mensuels</CalculatedLabel>
          <CalculatedAmount>{totalRevenues.toLocaleString()} FCFA</CalculatedAmount>
        </CalculatedValue>
      </FormSection>
    </>
  );
};
